// ── The month-end reforecast lock ──────────────────────────────────────────
//
// WHY THIS EXISTS, in the order the money moves:
//
//   1. Two business days before month end, the servicer forecasts the interest,
//      principal and fees owed for that month end.
//   2. The invoice goes to the borrower the same day, carrying that figure.
//   3. The borrower's account is ACH'd for that amount.
//
// Rebuilding the forward schedule after step 1 recomputes the SAME month end to
// a different number. The cash that arrives is the old figure; the schedule now
// says the new one; and nothing on either side is wrong, which is precisely what
// makes the reconciliation expensive. It is not a data error anyone can point
// at — it is two correct numbers for one month.
//
// So every route that can rebuild the schedule asks first, inside the window.
// Both pages that can — Portfolio Performance, where a reforecast is a SIDE
// EFFECT of solving IRRs, and Update Sched Pmts, where it is the whole job —
// share this file. They do not share their wording: on one page there is a safe
// alternative one click away, and on the other the reforecast IS the task and
// the question is only whether the invoices have already gone out.
//
// THIS WARNS, IT DOES NOT BLOCK. Re-running inside the window is sometimes
// exactly right — a rate was keyed wrong, a loan funded late, the invoices have
// not gone out yet. The person pressing the button knows which; this makes sure
// they know they are inside the window when they decide.

import { Modal, Btn } from "./ui";
import { INK_MUTED, INK_PRIMARY } from "./theme";
import { businessDaysToMonthEnd, inMonthEndLock } from "./monthEndLock.math.mjs";

// EVERY import AT THE TOP. The re-export below sat ABOVE this import, which put
// an `import` in the body of the module: legal to a bundler, and an ERROR under
// CRA's eslint (import/first), so the app would not compile. Not a style rule.
//
// The window arithmetic lives in monthEndLock.math.mjs so a test can import it
// without pulling React in, and is re-exported here so callers have one place
// to import from.
export { LOCK_BUSINESS_DAYS, LOCK_HOLIDAY_SLACK,
         businessDaysToMonthEnd, inMonthEndLock } from "./monthEndLock.math.mjs";

// "3 business days" reads as arithmetic; a date reads as a fact. The banner and
// the dialog both name the actual month end so nobody has to count.
export function monthEndLabel(d = new Date()) {
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return last.toLocaleDateString("en-US",
    { weekday: "long", month: "long", day: "numeric" });
}

const AMBER_BG = "#fdeccd", AMBER_LINE = "#e8c98a", AMBER_INK = "#7a4f00";

// The standing notice. Up for the whole window, wherever a reforecast can be
// started, so the dialog at click time is a confirmation rather than a surprise.
export const MonthEndNotice = ({ children, now }) => {
  if (!inMonthEndLock(now)) return null;
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start",
                  padding: "10px 14px", borderRadius: 8, marginTop: 10,
                  background: AMBER_BG, border: `1px solid ${AMBER_LINE}`,
                  fontSize: 13, color: AMBER_INK, lineHeight: 1.5 }}>
      <span aria-hidden="true">⚠️</span>
      <span>
        <strong>Month-end window.</strong>{" "}
        {monthEndLabel(now)} is the month end, and the servicer's forecast and the
        borrower invoices for it are due out two business days before.{" "}
        {children}
      </span>
    </div>
  );
};

// The dialog. Actions are supplied by the caller because the safe answer differs
// by page: on Portfolio Performance there is a solve that touches nothing, and
// on Update Sched Pmts there is only "not yet".
export const ReforecastWarning = ({ onClose, now, actions, lede }) => (
  <Modal title="Reforecasting inside the month-end window" onClose={onClose}>
    <div style={{ fontSize: 13.5, color: INK_PRIMARY, lineHeight: 1.6 }}>
      <p style={{ marginTop: 0 }}>{lede}</p>

      <div style={{ background: AMBER_BG, border: `1px solid ${AMBER_LINE}`,
                    borderRadius: 8, padding: "10px 14px", color: AMBER_INK,
                    marginBottom: 14 }}>
        <strong>{monthEndLabel(now)}</strong> is this month end
        {" "}&mdash; {businessDaysToMonthEnd(now)} business day
        {businessDaysToMonthEnd(now) === 1 ? "" : "s"} away, counting today.
      </div>

      <p style={{ marginTop: 0 }}>What a rebuild does inside this window:</p>
      <ul style={{ margin: "0 0 14px 0", paddingLeft: 20 }}>
        <li>Every future scheduled payment is recomputed off the latest forward
            curve &mdash; <strong>including this month end</strong>.</li>
        <li>If the servicer has already forecast that month end and the invoices
            have gone out, the borrower is being ACH'd for the <strong>old</strong>
            {" "}figure while the schedule now holds a <strong>new</strong> one.</li>
        <li>Neither number is wrong, which is what makes the difference expensive
            to unpick.</li>
      </ul>

      <p style={{ color: INK_MUTED, marginBottom: 18 }}>
        Go ahead if the invoices for this month have not gone out yet, or if you
        are fixing something that has to reach them. Otherwise it will keep until
        the first of the month.
        <br />
        <span style={{ fontSize: 12 }}>
          This window is counted in weekdays and does not know bank holidays, so
          it is deliberately a day wider than the rule.
        </span>
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {actions}
      </div>
    </div>
  </Modal>
);

// Kept here so the two pages cannot drift into two shades of "go ahead".
const stack = { display: "block", width: "100%", textAlign: "center" };
export const ProceedBtn = ({ onClick, children }) => (
  <span style={stack}><Btn onClick={onClick} variant="danger">{children}</Btn></span>
);
export const SafeBtn = ({ onClick, children }) => (
  <span style={stack}><Btn onClick={onClick} variant="primary">{children}</Btn></span>
);
export const CancelBtn = ({ onClick, children }) => (
  <span style={stack}><Btn onClick={onClick}>{children || "Cancel"}</Btn></span>
);
